<template>
  <div>
    <main-layout
      controlsBg="light"
      :breadcrumb="getBreadcrumb"
      :hasControlsBar="false">
      <div
        class="container margin-top-xl margin-left-l margin-right-l">
        <h1>
          <chevron
            @click="goBack()"/>
          {{ $t('variables.editionTitle') }}
        </h1>
        <div
          class="tags-selector">
          <treeselect
            class="tags-tree"
            v-model="tags"
            value-consists-of="LEAF_PRIORITY"
            :multiple="true"
            :options="getSelectionTreeOptions"/>
          <btn-pill
            class="btn-action"
            btn-type="secondary"
            btn-size="s"
            @click="add()">
            {{ $t('commons.add') }}
          </btn-pill>
          <btn-pill
            class="btn-action"
            btn-type="secondary"
            btn-size="s"
            @click="remove()">
            {{ $t('commons.remove') }}
          </btn-pill>
        </div>
        <div
          class="tags-controls margin-top-l margin-bottom-m">
          <checkbox
            id="all"
            :checked="allChecked"
            check-size="s"
            v-on:change="onSelectAllChange"/>
          <span>{{ $t('variables.selection') }}</span>
          <tags-filter
            v-model="filterTags"
            :tagsTree="getTagsTreeState"/>
        </div>
        <variable-table-selector
          ref="variableTableSelector"
          v-model="variables"
          :data="getData"
          :checkedVariableIds="checkedVariableIds"
          v-on:onRetrieveVariables="retrieveVariablesPage"
          v-on:checkedChange="checkedChange"/>
      </div>
    </main-layout>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import MainLayout from '@/layouts/MainLayout';
import Treeselect from '@riophae/vue-treeselect';
import VariableTableSelector from '@/components/variable/VariableTableSelector';
import BtnPill from '@/components/shared/buttons/BtnPill';
import Checkbox from '@/components/shared/forms/Checkbox';
import Chevron from '@/components/shared/Chevron';
import TagsFilter from '@/components/variable/TagsFilter';
import variableApi from '@/api/variable';

export default {
  name: 'VariableTag',
  components: {
    TagsFilter,
    Chevron,
    Checkbox,
    BtnPill,
    VariableTableSelector,
    Treeselect,
    MainLayout
  },
  data () {
    return {
      origin: '',
      variables: [],
      tags: [],
      filterTags: [],
      variableData: {},
      filter: '',
      page: 1,
      sort: {
        order: '',
        fieldName: ''
      },
      tablePageSize: 10,
      variablesCount: 0,
      checkedVariableIds: [],
      allChecked: false
    };
  },
  watch: {
    filterTags: {
      handler () {
        this.apiCall();
      },
      deep: true
    }
  },
  created () {
    const origin = this.$route.query.origin;
    if (origin) {
      this.origin = origin;
    }
    this.retrieveTagsTree({ context: this });
  },
  computed: {
    ...mapGetters([
      'getTagsTreeState'
    ]),
    getBreadcrumb () {
      switch (this.origin) {
        case 'Workspace':
          return [
            {name: this.$t('menu.workspace'), link: '/workspace'},
            {name: this.$t('variableManagement.title'), link: '/variable-management'},
            {name: this.$t('variables.taxonomy'), link: '/tags-tree'},
            {name: this.$t('variables.editionTitle'), disabled: true}
          ];
        case 'VariableManagement':
          return [
            {name: this.$t('menu.workspace'), link: '/workspace'},
            {name: this.$t('variableManagement.title'), link: '/variable-management'},
            {name: this.$t('variables.taxonomy'), link: '/tags-tree'},
            {name: this.$t('variables.editionTitle'), disabled: true}
          ];
        case 'ExplorerEvolutionVariables':
          return [
            {name: this.$t('menu.explorer'), link: '/explorer-evolution-variables'},
            {name: this.$t('variables.taxonomy'), link: '/tags-tree'},
            {name: this.$t('variables.editionTitle'), disabled: true}
          ];
        default:
          return [];
      }
    },
    getSelectionTreeOptions () {
      return createOptions(this.getTagsTreeState);
    },
    getFilterTreeOptions () {
      return createOptions(this.getTagsTreeState);
    },
    getData () {
      return this.variableData;
    }
  },
  methods: {
    ...mapActions([
      'retrieveTagsTree',
      'addTags',
      'removeTags'
    ]),
    goBack () {
      this.$router.push({name: 'VariableTagTree', query: {origin: this.origin}});
    },
    add () {
      if (this.checkedVariableIds.length > 0 && this.tags.length > 0) {
        this.addTags({context: this, variableIds: this.checkedVariableIds, tagIds: this.tags}).then(() => {
          this.apiCall();
        });
      }
    },
    remove () {
      if (this.checkedVariableIds.length > 0 && this.tags.length > 0) {
        this.removeTags({context: this, variableIds: this.checkedVariableIds, tagIds: this.tags}).then(() => {
          this.apiCall();
        });
      }
    },
    onSelectAllChange (event) {
      this.allChecked = event;
      if (event) {
        variableApi.getTaggedMinimalVariables(this, this.filterTags.join(), true, this.filter.toUpperCase()).then(data => {
          this.checkedVariableIds = data.body.map(variable => variable.id);
        });
      } else {
        this.checkedVariableIds = [];
      }
    },
    retrieveVariablesPage ({page, filter, sort}) {
      if (this.page !== page || this.filter !== filter || sort.fieldName !== this.sort.fieldName || sort.order !== this.sort.order) {
        this.page = page || 1;
        this.filter = filter;
        this.sort.fieldName = sort.fieldName;
        this.sort.order = sort.order;
        this.apiCall();
      }
    },
    apiCall () {
      variableApi.getPaginatedVariables(this, this.page, this.filter.toUpperCase(), this.sort.fieldName, this.sort.order, this.filterTags.join())
        .then(data => {
          this.variablesCount = data.body.count;
          this.variableData = {
            data: data.body.variables,
            pagination: {
              totalPages: data.body.totalPages,
              currentPage: data.body.currentPage,
              count: data.body.count
            }
          };
          this.allChecked = this.checkedVariableIds.length === this.variablesCount;
        });
    },
    checkedChange (id, event) {
      if (event) {
        this.checkedVariableIds.push(id);
      } else {
        const removeIndex = this.checkedVariableIds.indexOf(id);
        this.checkedVariableIds.splice(removeIndex, 1);
      }
      this.allChecked = this.checkedVariableIds.length === this.variablesCount;
    }
  }
};

const createOptions = (nodes) => {
  const options = [];
  for (let i = 0; i < nodes.length; i++) {
    const option = {
      id: nodes[i].data.id,
      label: nodes[i].text
    };

    if (nodes[i].children.length > 0) {
      option.children = createOptions(nodes[i].children);
    }

    options.push(option);
  }

  return options;
};

</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  h2 {
    color: $jamaicol;
  }

  .tags-selector {
    display: flex;
    align-items: center;
  }

  .tags-tree {
    width: 16rem;
  }

  .tags-controls {
    display: flex;
    align-items: center;
  }

  .tags-filter {
    display: flex;
    align-items: center;
    margin-left: auto;
  }

  .btn-action {
    margin-left: $sp-margin-small;
  }

</style>



// WEBPACK FOOTER //
// src/layouts/variables/VariableTag.vue